
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useTranslation } from 'react-i18next';

interface PriceRange {
  min: number;
  max: number;
}

interface KeyLevelsCardProps {
  supportResistance: {
    support: PriceRange[];
    resistance: PriceRange[];
  };
}

const formatPriceRange = (range: PriceRange): string => {
  return `${range.min.toLocaleString()} - ${range.max.toLocaleString()}`;
};

export const KeyLevelsCard = ({ supportResistance }: KeyLevelsCardProps) => {
  const { t } = useTranslation();
  
  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-amber-500/5 border border-border/50 backdrop-blur-sm hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-300 hover:-translate-y-1 group card-shine soft-glow">
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 via-transparent to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(245,158,11,0.08),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute inset-0 shimmer-slide opacity-0 group-hover:opacity-30 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      
      <CardHeader className="relative z-10 pb-4">
        <CardTitle className="text-foreground group-hover:text-amber-600 transition-colors duration-300 flex items-center gap-2">
          <div className="p-2 rounded-lg bg-amber-500/10 text-amber-500 group-hover:bg-amber-500/20 transition-all duration-300 floating-action">
            <TrendingUp className="h-5 w-5 icon-spin-hover" />
          </div>
          {t('market_analysis.support_resistance')}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-6">
        <div>
          <h4 className="text-sm font-semibold text-red-500 mb-3 flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse floating-action" />
            <TrendingDown className="h-4 w-4 icon-spin-hover" />
            {t('market_analysis.resistance')}
          </h4>
          <div className="space-y-2">
            {supportResistance.resistance.map((range, index) => (
              <div 
                key={index} 
                className="flex justify-between items-center p-3 bg-gradient-to-r from-red-500/10 via-red-500/5 to-transparent rounded-lg hover:from-red-500/20 hover:via-red-500/10 hover:to-red-500/5 transition-all duration-300 backdrop-blur-sm border border-red-500/20 hover:border-red-500/30 card-hover-effect gradient-animate"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <span className="text-foreground font-medium">R{index + 1}</span>
                <span className="text-red-500 font-semibold hover-scale">${formatPriceRange(range)}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h4 className="text-sm font-semibold text-green-500 mb-3 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse floating-action" />
            <TrendingUp className="h-4 w-4 icon-spin-hover" />
            {t('market_analysis.support')}
          </h4>
          <div className="space-y-2">
            {supportResistance.support.map((range, index) => (
              <div 
                key={index} 
                className="flex justify-between items-center p-3 bg-gradient-to-r from-green-500/10 via-green-500/5 to-transparent rounded-lg hover:from-green-500/20 hover:via-green-500/10 hover:to-green-500/5 transition-all duration-300 backdrop-blur-sm border border-green-500/20 hover:border-green-500/30 card-hover-effect gradient-animate"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <span className="text-foreground font-medium">S{index + 1}</span>
                <span className="text-green-500 font-semibold hover-scale">${formatPriceRange(range)}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
